import React, { useState } from 'react';
import { Package, Loader2, CreditCard, Check, LogIn } from 'lucide-react';
import { STRIPE_PRODUCTS } from '../stripe-config';
import { useStripeCheckout } from '../hooks/useStripeCheckout';
import { useAuth } from '../hooks/useAuth';

interface PremiumStoreProps {
  onClose: () => void;
}

export function PremiumStore({ onClose }: PremiumStoreProps) {
  const { createCheckoutSession, loading } = useStripeCheckout();
  const { user, loading: authLoading } = useAuth();
  const [processingProductId, setProcessingProductId] = useState<string | null>(null);

  const handlePurchase = async (productId: string, priceId: string, mode: 'payment' | 'subscription') => {
    if (!user) {
      return; // Prevent purchase if not logged in
    }

    setProcessingProductId(productId);
    
    try {
      const success_url = `${window.location.origin}?success=true`;
      const cancel_url = window.location.origin;
      
      await createCheckoutSession({
        price_id: priceId,
        success_url,
        cancel_url,
        mode,
      });
    } catch (error) {
      console.error('Purchase failed:', error);
    } finally {
      setProcessingProductId(null);
    }
  };

  const isDisabled = !user || authLoading || loading;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">Premium Packs</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6">
          {!user && !authLoading && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3">
                <LogIn className="w-5 h-5 text-yellow-600" />
                <p className="text-yellow-800 font-medium">
                  Please sign in to make a purchase
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {STRIPE_PRODUCTS.map((product) => (
              <div
                key={product.id}
                className="border border-gray-200 rounded-xl p-6 hover:border-yellow-500 transition-all duration-300 hover:shadow-xl"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Package className="w-6 h-6 text-yellow-500" />
                  <h3 className="text-xl font-semibold">{product.name}</h3>
                </div>
                
                <p className="text-gray-600 mb-4">{product.description}</p>

                {/* Gold checkmarks with features */}
                <div className="space-y-3 mb-6">
                  {product.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-5 h-5 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-gray-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between mt-auto">
                  <span className="text-2xl font-bold text-gray-900">{product.price}</span>
                  <button
                    onClick={() => handlePurchase(product.id, product.priceId, product.mode)}
                    disabled={isDisabled || processingProductId === product.id}
                    className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-4 py-2 rounded-lg font-medium hover:from-yellow-600 hover:to-yellow-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {processingProductId === product.id ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processing...
                      </>
                    ) : !user ? (
                      <>
                        <LogIn className="w-4 h-4" />
                        Sign In Required
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4" />
                        Purchase
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {STRIPE_PRODUCTS.length === 0 && (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No Products Available</h3>
              <p className="text-gray-500">Check back soon for new premium content!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}